// ─────────────────────────────────────────────────────────────────────────────
// FrequentlyBoughtBundles.jsx - Fully Responsive Design
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';

const BASE = 'https://evercorenutrition.com';

// ── Color Palette ─────────────────────────────────────────────────────────────
const C = {
  blue50:   '#eff6ff',
  blue100:  '#dbeafe',
  blue200:  '#bfdbfe',
  blue300:  '#93c5fd',
  blue400:  '#60a5fa',
  blue500:  '#3b82f6',
  blue600:  '#2563eb',
  blue700:  '#1d4ed8',
  blue800:  '#1e40af',

  green50:  '#f0fdf4',
  green100: '#dcfce7',
  green500: '#22c55e',
  green600: '#16a34a',
  green700: '#15803d',

  amber50:  '#fffbeb',
  amber100: '#fef3c7',
  amber400: '#fbbf24',
  amber500: '#f59e0b',
  amber600: '#d97706',

  purple50:  '#faf5ff',
  purple100: '#ede9fe',
  purple400: '#a78bfa',
  purple500: '#8b5cf6',
  purple600: '#7c3aed',

  red50:  '#fef2f2',
  red100: '#fee2e2',
  red500: '#ef4444',

  slate50:  '#f8fafc',
  slate100: '#f1f5f9',
  slate200: '#e2e8f0',
  slate300: '#cbd5e1',
  slate400: '#94a3b8',
  slate500: '#64748b',
  slate600: '#475569',
  slate700: '#334155',
  slate800: '#1e293b',
  slate900: '#0f172a',
};

// ── Responsive Hook ───────────────────────────────────────────────────────────
const useBreakpoint = () => {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return {
    isMobile:  width < 480,
    isTablet:  width >= 480 && width < 768,
    isDesktop: width >= 768,
    width,
  };
};

const getImageUrl = (url) => {
  if (!url) return '/assets/images/default-product.png';
  if (url.startsWith('http')) return url;
  return `${BASE}${url}`;
};

const resolveProductId = () => {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname || '';
  const match = path.match(/(\d+)(?:\/)?$/) || path.match(/product(?:-details)?\/(\d+)/i);
  return match ? match[1] : null;
};

const normalizeBundles = (incoming) => {
  if (!incoming) return [];
  if (Array.isArray(incoming)) return incoming;
  if (Array.isArray(incoming.bundles)) return incoming.bundles;
  if (Array.isArray(incoming.results)) return incoming.results;
  return [];
};

const buildBundleDetails = async (bundle) => {
  const frequentlyId = bundle?.frequently_id || bundle?.id;
  if (!frequentlyId) return bundle;

  try {
    const response = await fetch(`${BASE}/ev_admin/api/customer/frequently-bought/${frequentlyId}/`);
    if (!response.ok) return bundle;

    const data = await response.json();
    const detailBundle = data?.bundle || data || {};

    return {
      ...bundle,
      ...detailBundle,
      items: detailBundle.bundle_items || detailBundle.items || bundle.items || [],
      overall_discount_percent: detailBundle.overall_discount_percentage ?? bundle.overall_discount_percent ?? bundle.overall_discount_percentage ?? Number(detailBundle.bundle_discount || 0),
      total_discounted_price: detailBundle.total_discounted_price ?? bundle.total_discounted_price ?? 0,
      total_original_price: detailBundle.total_original_price ?? bundle.total_original_price ?? 0,
      item_count: detailBundle.item_count ?? bundle.item_count ?? (detailBundle.bundle_items?.length || bundle.items?.length || 0),
      bundle_name: detailBundle.bundle_name || bundle.bundle_name,
      bundle_discount: detailBundle.bundle_discount ?? bundle.bundle_discount,
    };
  } catch {
    return bundle;
  }
};

const getFallbackBundleItems = (bundle) => {
  if (!bundle) return [];

  const discountPercent = Number(bundle.overall_discount_percentage || bundle.bundle_discount || 0);
  const discountedTotal = Number(bundle.total_discounted_price || 0);
  const originalTotal = discountPercent > 0 && discountedTotal > 0
    ? discountedTotal / (1 - discountPercent / 100)
    : discountedTotal;

  return [{
    product_id: bundle.main_product || bundle.product_id || 0,
    product_name: bundle.main_product_name || bundle.bundle_name || 'Bundle Item',
    product_image: bundle.main_product_image || '',
    variant_name: bundle.main_product_name || 'Bundle Item',
    variant_weight: '',
    quantity: 1,
    price: Number(originalTotal || 0),
    discounted_price: Number(discountedTotal || 0),
  }];
};

// ── Bundle Card ───────────────────────────────────────────────────────────────
const BundleCard = ({ bundle, isAuthenticated, getCustomerId, showWarningMessage, index }) => {
  const { isMobile, isTablet, isDesktop, width } = useBreakpoint();

  const bundleItems = Array.isArray(bundle.items) && bundle.items.length > 0
    ? bundle.items
    : (Array.isArray(bundle.bundle_items) && bundle.bundle_items.length > 0
      ? bundle.bundle_items
      : (Array.isArray(bundle.products) && bundle.products.length > 0
        ? bundle.products
        : getFallbackBundleItems(bundle)));

  const [selectedItems, setSelectedItems] = useState(() => {
    const initial = {};
    bundleItems.forEach((_, idx) => { initial[idx] = true; });
    return initial;
  });
  const [adding, setAdding]       = useState(false);
  const [added, setAdded]         = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [cardHovered, setCardHovered] = useState(false);

  const selectedItemsList       = bundleItems.filter((_, idx) => selectedItems[idx]);
  const selectedCount           = selectedItemsList.length;
  const selectedOriginalPrice   = selectedItemsList.reduce((s, i) => s + (i.price || 0), 0);
  const selectedDiscountedPrice = selectedItemsList.reduce((s, i) => s + (i.discounted_price || i.price || 0), 0);
  const selectedSavings         = selectedOriginalPrice - selectedDiscountedPrice;
  const bundleOriginalPrice     = Number(bundle.total_original_price ?? selectedOriginalPrice ?? 0);
  const bundleDiscountedPrice   = Number(bundle.total_discounted_price ?? selectedDiscountedPrice ?? 0);
  const bundleSavings           = Number(bundle.total_savings ?? Math.max(bundleOriginalPrice - bundleDiscountedPrice, selectedSavings ?? 0));
  const isAllSelected           = selectedCount === bundleItems.length;
  const savingsPercent          = Math.round(bundle.overall_discount_percent || bundle.overall_discount_percentage || bundle.bundle_discount || 0);

  const accentPalette = [
    { from: C.blue600,   to: C.blue700,   light: C.blue50,   mid: C.blue100,   text: C.blue700   },
    { from: C.purple600, to: C.purple500, light: C.purple50, mid: C.purple100, text: C.purple600 },
    { from: C.amber500,  to: C.amber600,  light: C.amber50,  mid: C.amber100,  text: C.amber600  },
  ];
  const accent = accentPalette[index % accentPalette.length];

  // Responsive item card sizing
  const itemCardWidth = isMobile
    ? Math.min(130, (width - 80) / 2 - 10)   // 2 per row on mobile
    : isTablet
      ? 140
      : 150;

  const imageSize = isMobile ? itemCardWidth - 20 : isTablet ? 110 : 118;

  const handleSelectAll = () => {
    const newState = {};
    bundleItems.forEach((_, idx) => { newState[idx] = !isAllSelected; });
    setSelectedItems(newState);
  };

  const handleItemSelect = (idx) => {
    setSelectedItems(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const addSelectedToCart = async () => {
    if (selectedCount === 0) { showWarningMessage('Please select at least one item to add to cart'); return; }
    if (!isAuthenticated()) { showWarningMessage('Please login to add items to cart'); return; }

    const token      = localStorage.getItem('authToken');
    const customerId = getCustomerId();
    if (!customerId) { showWarningMessage('Could not identify customer. Please login again.'); return; }

    const frequentlyId = bundle.frequently_id || bundle.bundle_id || bundle.id || bundle.frequentlyId;
    if (!frequentlyId) { showWarningMessage('Bundle details are unavailable. Please try again.'); return; }

    setAdding(true);
    try {
      const response = await fetch(`${BASE}/ev_admin/api/customer/frequently-bought/add-to-cart/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_id: customerId,
          frequently_id: frequentlyId,
          quantity: 1,
        }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setAdded(true);
        const totalAdded = data.total_items_added || selectedCount || 0;
        const savedValue = Number(data.bundle_details?.bundle_discount || selectedSavings || 0);
        showWarningMessage(` ${totalAdded} item${totalAdded > 1 ? 's' : ''} added!`);
        window.dispatchEvent(new Event('cartUpdated'));
        setTimeout(() => setAdded(false), 3000);
      } else {
        showWarningMessage(data.message || 'Failed to add bundle. Please try again.');
      }
    } catch {
      showWarningMessage('Network error. Please try again.');
    } finally {
      setAdding(false);
    }
  };

  // ── Responsive padding ──────────────────────────────────────────────────────
  const cardPadding = isMobile ? '20px 16px 24px' : isTablet ? '24px 24px 28px' : '28px 32px 32px';
  const headerFontSize = isMobile ? '17px' : isTablet ? '19px' : '21px';

  return (
    <div
      onMouseEnter={() => setCardHovered(true)}
      onMouseLeave={() => setCardHovered(false)}
      style={{
        background: '#fff',
        borderRadius: isMobile ? '18px' : '24px',
        border: `1.5px solid ${cardHovered ? accent.from : C.slate200}`,
        boxShadow: cardHovered
          ? `0 24px 48px -12px rgba(37,99,235,0.18), 0 0 0 1px ${accent.mid}`
          : '0 8px 24px -6px rgba(0,0,0,0.08)',
        marginBottom: isMobile ? '20px' : '28px',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
        transform: cardHovered && isDesktop ? 'translateY(-5px)' : 'translateY(0)',
      }}
    >
      {/* Gradient Top Bar */}
      <div style={{
        height: '5px',
        background: `linear-gradient(90deg, ${accent.from}, ${accent.to}, ${C.blue300})`,
        width: '100%',
      }} />

      {/* Savings Badge */}
      {savingsPercent > 0 && (
        <div style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          background: `linear-gradient(135deg, ${C.green600}, ${C.green500})`,
          color: '#fff',
          borderRadius: '12px',
          padding: isMobile ? '5px 10px' : '6px 14px',
          fontSize: isMobile ? '11px' : '13px',
          fontWeight: '800',
          boxShadow: `0 4px 12px rgba(22,163,74,0.35)`,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          letterSpacing: '0.2px',
          whiteSpace: 'nowrap',
        }}>
          <span>🏷️</span> {savingsPercent}% OFF
        </div>
      )}

      <div style={{ padding: cardPadding }}>

        {/* ── Header ── */}
        <div style={{
          marginBottom: isMobile ? '18px' : '26px',
          paddingRight: savingsPercent > 0 ? (isMobile ? '90px' : '120px') : '0',
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            background: accent.light,
            border: `1px solid ${accent.mid}`,
            borderRadius: '20px',
            padding: '4px 12px',
            marginBottom: '8px',
          }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: accent.from, display: 'inline-block' }}></span>
            <span style={{ fontSize: '10px', fontWeight: '700', color: accent.text, textTransform: 'uppercase', letterSpacing: '1px' }}>Bundle Deal</span>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '10px',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{
                fontSize: headerFontSize,
                fontWeight: '800',
                color: C.slate900,
                margin: '0 0 4px',
                letterSpacing: '-0.3px',
                lineHeight: 1.2,
                wordBreak: 'break-word',
              }}>
                {bundle.bundle_name}
              </h3>
              <p style={{ fontSize: '12px', color: C.slate400, margin: 0, fontWeight: '500' }}>
                Complete your routine with these essentials
              </p>
            </div>

            {/* Select All Button */}
            <button
              onClick={handleSelectAll}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                cursor: 'pointer',
                fontSize: isMobile ? '12px' : '13px',
                fontWeight: '700',
                padding: isMobile ? '8px 14px' : '9px 20px',
                background: isAllSelected
                  ? `linear-gradient(135deg, ${accent.from}, ${accent.to})`
                  : C.slate100,
                color: isAllSelected ? '#fff' : C.slate600,
                border: isAllSelected ? 'none' : `1.5px solid ${C.slate200}`,
                borderRadius: '30px',
                transition: 'all 0.2s ease',
                boxShadow: isAllSelected ? `0 4px 12px rgba(37,99,235,0.3)` : 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              <i className={`fa-${isAllSelected ? 'solid fa-check-circle' : 'regular fa-circle'}`} style={{ fontSize: '13px' }} />
              {isMobile ? `All (${bundleItems.length})` : `Select All (${bundleItems.length})`}
            </button>
          </div>
        </div>

        {/* ── Items Row ───────────────────────────────────────────────────────── */}
        <div style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: isMobile ? '8px' : '12px',
          flexWrap: isMobile && bundleItems.length > 2 ? 'wrap' : 'nowrap',
          overflowX: isMobile && bundleItems.length <= 2 ? 'visible' : (isMobile ? 'visible' : 'auto'),
          marginBottom: isMobile ? '18px' : '26px',
          justifyContent: 'center',
          padding: isMobile ? '14px 10px' : isTablet ? '16px' : '20px',
          background: `linear-gradient(135deg, ${C.slate50}, #fff)`,
          borderRadius: isMobile ? '14px' : '18px',
          border: `1px solid ${C.slate100}`,
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}>
          {bundleItems.map((item, idx) => (
            <React.Fragment key={`${item.variant_id || item.product_id || idx}-${idx}`}>

              {/* ── Item Card ── */}
              <div
                style={{
                  width: `${itemCardWidth}px`,
                  minWidth: `${itemCardWidth}px`,
                  maxWidth: `${itemCardWidth}px`,
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  padding: isMobile ? '8px 4px' : '10px 6px',
                  position: 'relative',
                  opacity: selectedItems[idx] ? 1 : 0.45,
                  transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                  transform: hoveredItem === idx && isDesktop ? 'translateY(-6px)' : 'translateY(0)',
                }}
                onMouseEnter={() => setHoveredItem(idx)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                {/* Checkbox */}
                <label style={{
                  position: 'absolute',
                  top: '2px',
                  left: '2px',
                  cursor: 'pointer',
                  zIndex: 3,
                  background: selectedItems[idx]
                    ? `linear-gradient(135deg, ${accent.from}, ${accent.to})`
                    : '#fff',
                  borderRadius: '50%',
                  width: isMobile ? '26px' : '30px',
                  height: isMobile ? '26px' : '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: selectedItems[idx]
                    ? `0 3px 10px rgba(37,99,235,0.35)`
                    : '0 2px 6px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  border: selectedItems[idx] ? 'none' : `2px solid ${C.slate200}`,
                }}>
                  <input
                    type="checkbox"
                    checked={selectedItems[idx]}
                    onChange={() => handleItemSelect(idx)}
                    style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: accent.from }}
                  />
                </label>

                {/* Image */}
                <div style={{
                  width: `${imageSize}px`,
                  height: `${imageSize}px`,
                  flexShrink: 0,
                  borderRadius: isMobile ? '14px' : '18px',
                  border: selectedItems[idx]
                    ? `2.5px solid ${accent.from}`
                    : `1.5px solid ${C.slate200}`,
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${accent.light} 0%, #fff 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: isMobile ? '10px' : '14px',
                  transition: 'all 0.3s ease',
                  boxShadow: selectedItems[idx]
                    ? `0 8px 20px rgba(37,99,235,0.15)`
                    : '0 2px 8px rgba(0,0,0,0.04)',
                  position: 'relative',
                  marginTop: isMobile ? '10px' : '14px',
                }}>
                  <img
                    src={getImageUrl(item.product_image)}
                    alt={item.product_name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      transition: 'transform 0.3s ease',
                      transform: hoveredItem === idx && isDesktop ? 'scale(1.08)' : 'scale(1)',
                    }}
                    onError={(e) => { e.currentTarget.src = '/assets/images/default-product.png'; }}
                  />
                  {hoveredItem === idx && isDesktop && (
                    <div style={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      background: `linear-gradient(to top, ${accent.from}cc, transparent)`,
                      padding: '8px', textAlign: 'center',
                      color: '#fff', fontSize: '9px', fontWeight: '700', letterSpacing: '0.5px',
                    }}>
                      {/* QUICK VIEW */}
                    </div>
                  )}
                </div>

                {/* Product Name */}
                <div style={{
                  textAlign: 'center',
                  width: '100%',
                  minHeight: isMobile ? '36px' : '42px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}>
                  <p style={{
                    fontSize: isMobile ? '11px' : '13px',
                    fontWeight: '700',
                    color: C.slate800,
                    margin: '0 0 2px',
                    lineHeight: 1.3,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    {item.product_name}
                  </p>
                  <p style={{ fontSize: '10px', color: C.slate400, margin: 0, fontWeight: '500' }}>
                    {item.variant_name}{item.variant_weight ? ` · ${item.variant_weight}` : ''}
                  </p>
                </div>

                {/* Price */}
                <div style={{ textAlign: 'center' }}>
                  <span style={{ fontSize: isMobile ? '13px' : '15px', fontWeight: '800', color: accent.text }}>
                    ₹{Math.round(item.discounted_price || item.price || 0).toLocaleString('en-IN')}
                  </span>
                  {item.price > (item.discounted_price || item.price) && (
                    <del style={{ fontSize: '10px', color: C.slate400, marginLeft: '5px' }}>
                      ₹{Math.round(item.price).toLocaleString('en-IN')}
                    </del>
                  )}
                </div>

                {/* Qty Badge */}
                {item.quantity > 1 && (
                  <span style={{
                    fontSize: '10px',
                    color: '#fff',
                    background: `linear-gradient(135deg, ${accent.from}, ${accent.to})`,
                    borderRadius: '20px',
                    padding: '2px 8px',
                    fontWeight: '700',
                    boxShadow: `0 2px 6px rgba(37,99,235,0.3)`,
                  }}>
                    ×{item.quantity}
                  </span>
                )}
              </div>

              {/* Plus Separator */}
              {idx < bundleItems.length - 1 && (
                <div style={{
                  flexShrink: 0,
                  alignSelf: 'center',
                  width: isMobile ? '26px' : '32px',
                  height: isMobile ? '26px' : '32px',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${accent.light}, ${accent.mid})`,
                  border: `1.5px solid ${accent.mid}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: accent.from,
                  fontSize: isMobile ? '16px' : '20px',
                  fontWeight: '700',
                  boxShadow: `0 2px 8px rgba(37,99,235,0.12)`,
                }}>
                  +
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Divider */}
        <div style={{
          height: '1.5px',
          background: `linear-gradient(90deg, transparent, ${accent.mid}, ${C.blue200}, ${accent.mid}, transparent)`,
          marginBottom: isMobile ? '16px' : '22px',
          borderRadius: '2px',
        }} />

        {/* ── Pricing Row ─────────────────────────────────────────────────────── */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? '14px' : '18px',
        }}>
          {/* Price Block */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{
                fontSize: isMobile ? '28px' : isTablet ? '30px' : '34px',
                fontWeight: '800',
                background: `linear-gradient(135deg, ${accent.from}, ${accent.to})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                lineHeight: 1,
              }}>
                ₹{Math.round(isAllSelected ? bundleDiscountedPrice : selectedDiscountedPrice).toLocaleString('en-IN')}
              </span>
              {(isAllSelected ? bundleOriginalPrice : selectedOriginalPrice) > (isAllSelected ? bundleDiscountedPrice : selectedDiscountedPrice) && (
                <span style={{
                  fontSize: '18px',
                  color: C.slate400,
                  fontWeight: '600',
                  textDecoration: 'line-through',
                  textDecorationThickness: '2px',
                  marginLeft: '2px',
                }}>
                  ₹{Math.round(isAllSelected ? bundleOriginalPrice : selectedOriginalPrice).toLocaleString('en-IN')}
                </span>
              )}
              <span style={{
                fontSize: '11px',
                color: C.slate500,
                background: C.slate100,
                padding: '3px 10px',
                borderRadius: '20px',
                fontWeight: '600',
                border: `1px solid ${C.slate200}`,
              }}>
                {selectedCount} item{selectedCount !== 1 ? 's' : ''}
              </span>
            </div>

            {(isAllSelected ? bundleSavings : selectedSavings) > 0 && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: C.green50,
                border: `1px solid ${C.green100}`,
                borderRadius: '10px',
                padding: '5px 12px',
                width: 'fit-content',
              }}>
                <span style={{
                  width: '7px', height: '7px', borderRadius: '50%',
                  background: C.green500, display: 'inline-block', flexShrink: 0,
                }}></span>
                <span style={{ fontSize: isMobile ? '12px' : '13px', color: C.green700, fontWeight: '700' }}>
                  You Save ₹{Math.round(isAllSelected ? bundleSavings : selectedSavings).toLocaleString('en-IN')}!
                </span>
              </div>
            )}
          </div>

          {/* Add to Cart Button */}
          <button
            onClick={addSelectedToCart}
            disabled={adding || selectedCount === 0}
            style={{
              padding: isMobile ? '14px 20px' : '15px 34px',
              background: added
                ? `linear-gradient(135deg, ${C.green600}, ${C.green700})`
                : selectedCount === 0
                  ? C.slate200
                  : `linear-gradient(135deg, ${accent.from}, ${accent.to})`,
              color: selectedCount === 0 ? C.slate400 : '#fff',
              border: 'none',
              borderRadius: '50px',
              fontSize: isMobile ? '14px' : '15px',
              fontWeight: '800',
              cursor: (adding || selectedCount === 0) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              transition: 'all 0.3s ease',
              opacity: adding ? 0.8 : 1,
              width: isMobile ? '100%' : 'auto',
              minWidth: isMobile ? 'unset' : '210px',
              justifyContent: 'center',
              boxShadow: selectedCount > 0
                ? added
                  ? `0 6px 20px rgba(22,163,74,0.4)`
                  : `0 6px 20px rgba(37,99,235,0.35)`
                : 'none',
              letterSpacing: '0.2px',
            }}
            onMouseEnter={(e) => {
              if (!adding && selectedCount > 0 && !added && isDesktop) {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = `0 10px 28px rgba(37,99,235,0.45)`;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = selectedCount > 0
                ? `0 6px 20px rgba(37,99,235,0.35)` : 'none';
            }}
          >
            {adding ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status"
                  style={{ width: '16px', height: '16px', borderWidth: '2px' }} />
                Adding...
              </>
            ) : added ? (
              <>✅ Added to Cart!</>
            ) : (
              <><span>🛒</span> Add Selected ({selectedCount})</>
            )}
          </button>
        </div>

        {/* Zero-selection tip */}
        {selectedCount === 0 && (
          <div style={{
            marginTop: '16px',
            padding: isMobile ? '11px 14px' : '13px 20px',
            background: C.amber50,
            borderRadius: '12px',
            fontSize: '13px',
            color: C.amber600,
            textAlign: 'center',
            fontWeight: '600',
            border: `1px solid ${C.amber100}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}>
            <span>💡</span> Select items to unlock your bundle savings!
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Export ───────────────────────────────────────────────────────────────
const FrequentlyBoughtBundles = ({ bundles, isAuthenticated, getCustomerId, showWarningMessage }) => {
  const { isMobile, isTablet } = useBreakpoint();
  const [bundleList, setBundleList] = useState(() => normalizeBundles(bundles));

  useEffect(() => {
    const productId = resolveProductId();
    if (!productId) {
      setBundleList(normalizeBundles(bundles));
      return;
    }

    let isMounted = true;
    const fetchBundles = async () => {
      try {
        const response = await fetch(`https://evercorenutrition.com/ev_admin/api/customer/frequently-bought/?product_id=${productId}`);
        if (!response.ok) {
          if (isMounted) setBundleList(normalizeBundles(bundles));
          return;
        }

        const data = await response.json();
        const summaryBundles = normalizeBundles(data);

        if (!summaryBundles.length) {
          if (isMounted) setBundleList([]);
          return;
        }

        const detailedBundles = await Promise.all(summaryBundles.map(buildBundleDetails));
        if (isMounted) setBundleList(detailedBundles);
      } catch {
        if (isMounted) setBundleList(normalizeBundles(bundles));
      }
    };

    fetchBundles();
    return () => { isMounted = false; };
  }, [bundles]);

  if (!bundleList || bundleList.length === 0) return null;

  return (
    <section style={{
      padding: isMobile ? '40px 0 48px' : isTablet ? '52px 0 60px' : '64px 0 72px',
      background: `linear-gradient(160deg, ${C.blue50} 0%, #fff 40%, ${C.purple50} 100%)`,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Decorative blobs — hidden on mobile to keep it clean */}
      {!isMobile && (
        <>
          <div style={{
            position: 'absolute', top: '-80px', right: '-80px',
            width: '320px', height: '320px', borderRadius: '50%',
            background: `radial-gradient(circle, ${C.blue100} 0%, transparent 70%)`,
            pointerEvents: 'none', opacity: 0.6,
          }} />
          <div style={{
            position: 'absolute', bottom: '-60px', left: '-60px',
            width: '260px', height: '260px', borderRadius: '50%',
            background: `radial-gradient(circle, ${C.purple100} 0%, transparent 70%)`,
            pointerEvents: 'none', opacity: 0.5,
          }} />
        </>
      )}

      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? '0 14px' : isTablet ? '0 20px' : '0 24px',
        position: 'relative',
      }}>

        {/* ── Section Header ──────────────────────────────────────────────────── */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? '32px' : isTablet ? '40px' : '52px' }}>
          {/* Pill label */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: `linear-gradient(135deg, ${C.blue600}, ${C.blue700})`,
            padding: isMobile ? '6px 16px' : '7px 22px',
            borderRadius: '50px',
            marginBottom: isMobile ? '16px' : '22px',
            boxShadow: `0 4px 14px rgba(37,99,235,0.35)`,
          }}>
            <span style={{
              fontSize: isMobile ? '10px' : '12px',
              letterSpacing: '1.5px',
              color: C.amber400,
              textTransform: 'uppercase',
              fontWeight: '800',
            }}>
              ✨ SAVE MORE TOGETHER ✨
            </span>
          </div>

          <h2 style={{
            fontSize: isMobile ? '26px' : isTablet ? '34px' : '44px',
            fontWeight: '900',
            background: `linear-gradient(135deg, ${C.slate900} 0%, ${C.blue700} 60%, ${C.blue500} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            margin: '0 0 12px',
            letterSpacing: '-0.5px',
            lineHeight: 1.1,
            padding: isMobile ? '0 8px' : '0',
          }}>
            Frequently Bought Together
          </h2>

          {/* Accent line */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            margin: '0 auto 16px',
            width: 'fit-content',
          }}>
            <div style={{ width: '30px', height: '3px', background: C.blue300, borderRadius: '3px' }} />
            <div style={{
              width: '9px', height: '9px', borderRadius: '50%',
              background: `linear-gradient(135deg, ${C.blue600}, ${C.blue400})`,
              boxShadow: `0 0 8px ${C.blue400}`,
            }} />
            <div style={{ width: '30px', height: '3px', background: C.blue300, borderRadius: '3px' }} />
          </div>

          <p style={{
            fontSize: isMobile ? '13px' : '15px',
            color: C.slate500,
            maxWidth: '540px',
            margin: '0 auto',
            lineHeight: '1.65',
            fontWeight: '500',
            padding: isMobile ? '0 8px' : '0',
          }}>
            Complete your purchase with these perfectly paired items and enjoy exclusive bundle savings
          </p>

          {/* Stats strip */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            marginTop: '16px',
            background: C.green50,
            border: `1px solid ${C.green100}`,
            borderRadius: '20px',
            padding: '7px 18px',
          }}>
            <span style={{ fontSize: isMobile ? '12px' : '13px', color: C.green700, fontWeight: '700' }}>
              🔥 {bundleList.length} exclusive bundle{bundleList.length > 1 ? 's' : ''} available
            </span>
          </div>
        </div>

        {/* Bundle Cards */}
        {bundleList.map((bundle, idx) => (
          <div
            key={bundle.bundle_id || bundle.frequently_id || bundle.id || idx}
            style={{
              animation: 'fbtFadeUp 0.55s ease-out both',
              animationDelay: `${idx * 0.12}s`,
            }}
          >
            <BundleCard
              bundle={bundle}
              index={idx}
              isAuthenticated={isAuthenticated}
              getCustomerId={getCustomerId}
              showWarningMessage={showWarningMessage}
            />
          </div>
        ))}
      </div>

      <style>{`
        @keyframes fbtFadeUp {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        /* Hide scrollbar for items row on mobile */
        .fbt-items-row::-webkit-scrollbar { display: none; }
        @media (max-width: 480px) {
          .fbt-savings-badge { top: 12px !important; right: 12px !important; }
        }
        @media (hover: none) {
          /* Disable hover transforms on touch devices */
          .fbt-card:hover { transform: none !important; }
        }
      `}</style>
    </section>
  );
};

export default FrequentlyBoughtBundles;
